import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Plus, Edit2, Trash2, X } from 'lucide-react';

const PageHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px;
  h1 { font-size: 2rem; }
`;

const Button = styled.button`
  background: var(--primary); color: white; border: none; padding: 10px 20px;
  border-radius: 8px; font-weight: 600; display: flex; align-items: center; gap: 8px; cursor: pointer;
  &:hover { opacity: 0.9; }
  &.secondary { background: transparent; border: 1px solid var(--glass-border); color: var(--text-main); }
  &.danger { background: #ef4444; }
`;

const Table = styled.table`
  width: 100%; border-collapse: collapse; background: var(--bg-card); border-radius: 12px;
  overflow: hidden; border: 1px solid var(--glass-border);
  th, td { padding: 16px; text-align: left; border-bottom: 1px solid var(--glass-border); }
  th { background: rgba(99, 102, 241, 0.05); font-weight: 600; color: var(--text-muted); }
  .actions { display: flex; gap: 10px; }
  .icon-btn { background: transparent; border: none; color: var(--text-muted); cursor: pointer; padding: 8px; border-radius: 6px; }
  .icon-btn:hover { background: var(--input-bg); color: var(--text-main); }
  .icon-btn.delete:hover { color: #ef4444; background: rgba(239, 68, 68, 0.1); }
`;

const ModalOverlay = styled.div`
  position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center; z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--bg-card); width: 90%; max-width: 600px; border-radius: 16px; padding: 30px; border: 1px solid var(--glass-border);
  .modal-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; h2{font-size:1.5rem} }
  .modal-header button { background: transparent; border: none; color: var(--text-muted); cursor: pointer; }
`;

const Form = styled.form`
  display: flex; flex-direction: column; gap: 20px;
  .form-group { display: flex; flex-direction: column; gap: 8px; }
  .form-group label { font-weight: 500; color: var(--text-muted); }
  .form-group input, .form-group select { padding: 12px 16px; background: var(--input-bg); border: 1px solid var(--glass-border); border-radius: 8px; color: var(--text-main); font-family: inherit;}
  .form-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 10px; }
`;

const ManageTechnologies = () => {
    const [techs, setTechs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [currentTech, setCurrentTech] = useState({ name: '', icon: '', category: 'Frontend', link: '' });
    const [isEditing, setIsEditing] = useState(false);

    const fetchTech = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/technologies`);
            const data = await response.json();
            setTechs(Array.isArray(data) ? data : []);
        } catch (err) { console.error(err); } finally { setLoading(false); }
    };

    useEffect(() => { fetchTech(); }, []);

    const handleOpenModal = (item = null) => {
        setCurrentTech(item || { name: '', icon: '', category: 'Frontend', link: '' });
        setIsEditing(!!item);
        setModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('adminToken');
        const url = isEditing ? `${process.env.REACT_APP_API_URL || ''}/api/technologies/${currentTech.id}` : `${process.env.REACT_APP_API_URL || ''}/api/technologies`;
        try {
            const response = await fetch(url, {
                method: isEditing ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(currentTech)
            });
            if (response.ok) { fetchTech(); setModalOpen(false); }
            else alert('Save failed');
        } catch (err) { alert('Server error'); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this technology?')) return;
        try {
            await fetch(`${process.env.REACT_APP_API_URL || ''}/api/technologies/${id}`, {
                method: 'DELETE', headers: { 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` }
            });
            fetchTech();
        } catch (err) { alert('Server error'); }
    };

    return (
        <div>
            <PageHeader>
                <h1>Manage Technologies</h1>
                <Button onClick={() => handleOpenModal()}><Plus size={20} /> Add Tech</Button>
            </PageHeader>

            {loading ? <div>Loading...</div> : (
                <Table>
                    <thead><tr><th>Name</th><th>Category</th><th>Link</th><th>Icon Path/URL</th><th>Actions</th></tr></thead>
                    <tbody>
                        {techs.map(item => (
                            <tr key={item.id}>
                                <td>{item.name}</td>
                                <td>{item.category}</td>
                                <td>{item.link}</td>
                                <td>{item.icon}</td>
                                <td className="actions">
                                    <button className="icon-btn" onClick={() => handleOpenModal(item)}><Edit2 size={18} /></button>
                                    <button className="icon-btn delete" onClick={() => handleDelete(item.id)}><Trash2 size={18} /></button>
                                </td>
                            </tr>
                        ))}
                        {techs.length === 0 && <tr><td colSpan="4" style={{ textAlign: 'center' }}>No technologies found</td></tr>}
                    </tbody>
                </Table>
            )}

            {modalOpen && (
                <ModalOverlay onClick={() => setModalOpen(false)}>
                    <ModalContent onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{isEditing ? 'Edit Tech' : 'New Tech'}</h2>
                            <button onClick={() => setModalOpen(false)}><X size={24} /></button>
                        </div>
                        <Form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>Technology Name</label>
                                <input type="text" value={currentTech.name} onChange={e => setCurrentTech({ ...currentTech, name: e.target.value })} required />
                            </div>
                            <div className="form-group">
                                <label>Category</label>
                                <select value={currentTech.category} onChange={e => setCurrentTech({ ...currentTech, category: e.target.value })}>
                                    <option value="Frontend">Frontend</option>
                                    <option value="Backend">Backend</option>
                                    <option value="Infrastructure">Infrastructure</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Icon URL (or class name)</label>
                                <input type="text" value={currentTech.icon} onChange={e => setCurrentTech({ ...currentTech, icon: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Link (e.g., /tech/react)</label>
                                <input type="text" value={currentTech.link || ''} onChange={e => setCurrentTech({ ...currentTech, link: e.target.value })} />
                            </div>
                            <div className="form-actions">
                                <Button type="button" className="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
                                <Button type="submit">{isEditing ? 'Save' : 'Create'}</Button>
                            </div>
                        </Form>
                    </ModalContent>
                </ModalOverlay>
            )}
        </div>
    );
};
export default ManageTechnologies;
